import { createSelector } from "@reduxjs/toolkit";
import { ZViewSnapshotState, ZViewerView, ZRoomColoringId, type IViewSnapshotState, type IViewerView } from "@multiconsult-group/multiplacer-core";
import type { IRootState } from "../app/store";

const selectedKeys = (map: Partial<Record<string, boolean>>) =>
  Object.keys(map).filter(key => map[key] === true).sort();

export const selectRoomValidationViewSettings = createSelector(
  [(state: IRootState) => state.design.drofusStandardRoomDiscipline,
    (state: IRootState) => state.drofusOccurrenceValidation],
  (discipline, filters): IViewerView["modules"]["ROOM_VALIDATION"] => ({
    occurrences: {
      filterMode: filters.filterMode,
      listType: filters.specifiedArticlesListType,
      responsibility: filters.specifiedArticlesResponsibility,
      tableFilter: filters.tableFilters.articles,
    },
    standardRoom: {
      discipline: discipline === "standardRoom.E" ? "E" : discipline === "standardRoom.V" ? "V" : null,
      tableFilter: filters.tableFilters.standardRoom,
    },
  }),
);

/** Explicit allowlist. New required schema fields must be added here and in viewThunks. */
export const selectViewerView = createSelector(
  [(state: IRootState) => state.design.mcbimItemsFilterMode,
    (state: IRootState) => state.design.ifcClassesToDisplay,
    (state: IRootState) => state.design.disciplinesToDisplay,
    (state: IRootState) => state.design.mcbimItemColorMode,
    (state: IRootState) => state.design.activeRoomColoringId, selectRoomValidationViewSettings,
    (state: IRootState) => state.design.activeArticleColoringId,
    (state: IRootState) => state.design.selectedRoomTab],
  (mode, classes, disciplines, componentColorMode, roomColoringId, validation, articleColoringId, roomPanelTab): IViewerView => {
    const view: IViewerView = {
      schemaVersion: 1,
      modules: { ROOM_VALIDATION: validation },
      roomPanelTab: roomPanelTab ?? null,
      componentColorMode,
      articleColoringId: articleColoringId ?? null,
      roomColoringId: roomColoringId ? ZRoomColoringId.parse(roomColoringId) : null,
      visibility: mode === "INSTANCE_CLASS"
        ? { mode, ifcClasses: [...classes].sort((a, b) => a.ifcClass.localeCompare(b.ifcClass)) }
        : { mode, disciplines: selectedKeys(disciplines) },
    };
    return ZViewerView.parse(view);
  },
);

/** Null means the view is not ready to share; never invent a floor or camera. */
export const selectSharedViewState = createSelector(
  [selectViewerView,
    (state: IRootState) => state.design.activeModule,
    (state: IRootState) => state.design.floorAndJob,
    (state: IRootState) => state.design.rotation,
    (state: IRootState) => state.design.selectedSpaceNumber,
    (state: IRootState) => state.viewer.currentCamera],
  (view, module, floor, rotation, selectedSpaceNumber, currentCamera): IViewSnapshotState | null => {
    if (!module || !floor?.building?.trim() || !currentCamera || currentCamera.floorGlobalId !== floor.floorGlobalId) return null;
    const snapshot: IViewSnapshotState = {
      view,
      context: {
        module,
        floorName: floor.floorName,
        building: floor.building.trim(),
        camera: {
          rotation,
          zoom: currentCamera.camera.zoom,
          centerWorld: { ...currentCamera.camera.centerWorld },
        },
        ...(selectedSpaceNumber ? { selectedSpaceNumber } : {}),
      },
    };
    // Camera updates can be transiently invalid; sharing must not break rendering.
    const result = ZViewSnapshotState.safeParse(snapshot);
    return result.success ? result.data : null;
  },
);
